import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Pagination,
  Box,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Snackbar,
  Stack,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  getEstadisticasAsistencia,
  getAsistenciasEvento,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { getErrorMessage } from './EventosEmpleabilidad.helpers';
import styleScss from './styles.module.scss';

export interface AsistenteRow {
  id: number;
  user_id: number;
  tipo_documento: string;
  numero_documento: string;
  nombre_completo: string;
  realizo_prerregistro: 'SI' | 'NO';
  fecha_prerregistro?: string | null;
  fecha_asistencia?: string | null;
  metodo_confirmacion?: string | null;
}

export interface EstadisticasAsistencia {
  total_prerregistrados: number;
  total_asistentes: number;
  asistentes_con_prerregistro: number;
  asistentes_sin_prerregistro: number;
}

export const VerCandidatosAsistentesEventoPage: React.FC = () => {
  const params = useParams<{ id?: string; eventoId?: string; idEvento?: string }>();
  const id = params.id || params.eventoId || params.idEvento;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filtroPrerregistro, setFiltroPrerregistro] = useState<string>('Cualquiera');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [asistentes, setAsistentes] = useState<AsistenteRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [estadisticas, setEstadisticas] = useState<EstadisticasAsistencia | null>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [alert, setAlert] = useState<boolean>(true);
  const [alertMsg, setAlertMsg] = useState<string>('');

  const notify = (msg: string, success = true) => {
    setAlert(success);
    setAlertMsg(msg);
    setOpen(true);
    setTimeout(() => setOpen(false), 3000);
  };

  const limpiarFiltros = () => {
    setSearchTerm('');
    setFiltroPrerregistro('Cualquiera');
    setPage(1);
  };

  const fetchEstadisticas = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getEstadisticasAsistencia(id);
      const payload = res?.data || res;
      setEstadisticas(payload?.data || payload);
    } catch (err: any) {
      console.error('Error al cargar estadísticas:', err);
      notify(getErrorMessage(err), false);
    }
  }, [id]);

  const fetchAsistencias = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    try {
      const paramsQuery: Record<string, any> = { page, limit: 10 };

      if (searchTerm.trim()) paramsQuery.search = searchTerm.trim();
      if (filtroPrerregistro !== 'Cualquiera') paramsQuery.realizo_prerregistro = filtroPrerregistro;

      const res = await getAsistenciasEvento(id, paramsQuery);
      const payload = res?.data || res;

      setAsistentes(payload?.data || []);
      setTotalPages(payload?.totalPages || 1);
    } catch (err: any) {
      console.error('Error consultando asistentes:', err);
      setAsistentes([]);
      notify(getErrorMessage(err), false);
    } finally {
      setLoading(false);
    }
  }, [id, page, searchTerm, filtroPrerregistro]);

  useEffect(() => {
    fetchEstadisticas();
  }, [fetchEstadisticas]);

  useEffect(() => {
    fetchAsistencias();
  }, [fetchAsistencias]);

  const formatFecha = (fechaStr?: string | null): string => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr.includes('T') ? fechaStr : fechaStr.replace(/-/g, '/'));
    if (isNaN(fecha.getTime())) return fechaStr;

    const day = String(fecha.getDate()).padStart(2, '0');
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const year = String(fecha.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  return (
    <>
    <PageTitle breadcrumbs={[]}>Ver Candidatos Asistentes Evento</PageTitle>

      <div
        className="p-4 shadow-4 rounded-3 mt-15"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0px 3px 6px #00000029',
        }}
      >
        <LoadingBackdrop loading={loading} />

        {/* Encabezado Principal y Botón Regresar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BackButton />

            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                color: '#000305',
                fontSize: '1.25rem',
              }}
            >
              VER CANDIDATOS ASISTENTES EVENTO
            </Typography>
          </Box>
        </Box>

        {/* TARJETAS RESUMEN (Estadísticas del Evento) */}
        {estadisticas && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  borderColor: '#E2E8F0',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
                  >
                    Total Prerregistrados
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      color: '#2C3E50',
                    }}
                  >
                    {estadisticas.total_prerregistrados}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  borderColor: '#E2E8F0',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
                  >
                    Total Asistentes
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      color: '#2C3E50',
                    }}
                  >
                    {estadisticas.total_asistentes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  borderColor: '#E2E8F0',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
                  >
                    Asistieron con Prerregistro
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      color: '#2C3E50',
                    }}
                  >
                    {estadisticas.asistentes_con_prerregistro}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  borderColor: '#E2E8F0',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
                  >
                    Asistieron sin Prerregistro
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      color: '#2C3E50',
                    }}
                  >
                    {estadisticas.asistentes_sin_prerregistro}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* CONTROLES DE FILTRO */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    color: '#020305',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Prerregistro
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={filtroPrerregistro}
                  exclusive
                  onChange={(_, val) => val && setFiltroPrerregistro(val)}
                  sx={{
                    height: 36,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      fontFamily: 'Poppins, sans-serif',
                      textTransform: 'none',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      flex: { xs: 1, sm: 'initial' },
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      color: '#2C3E50',
                      '&.Mui-selected': {
                        backgroundColor: '#2266CC',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#1B52A4',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="Cualquiera">Cualquiera</ToggleButton>
                  <ToggleButton value="SI">Sí</ToggleButton>
                  <ToggleButton value="NO">No</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

          <Grid item xs={12} sm>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Número documento / Nombre completo"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                sx={{
                  backgroundColor: '#FFF',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14.5px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                  },
                }}
              />

                <button
                  type="button"
                  onClick={limpiarFiltros}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: '0 16px',
                    border: '1px solid #2266CC',
                    borderRadius: '4px',
                    backgroundColor: '#FFFFFF',
                    color: '#2266CC',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Limpiar filtros
                </button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* TABLA DE CANDIDATOS */}
        <div
          className={`${styleScss['table-limit']}`}
          style={{
            maxWidth: '100%',
            overflowWrap: 'break-word',
            overflowY: 'hidden',
          }}
        >
          <Stack padding={2}>
            <TableContainer
              component={Paper}
              variant="outlined"
              style={{
                overflowY: 'visible',
                overflowWrap: 'break-word',
                overflowX: 'auto',
              }}
              sx={{ borderRadius: '4px' }}
            >
              <Table
                style={{
                  overflowY: 'visible',
                  minWidth: '700px',
                }}
              >
                <TableHead
                  className={`${styleScss['th-custom']}`}
                  sx={{
                    backgroundColor: '#2266CC',
                  }}
                >
                  <TableRow>
                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Tipo de documento</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Número de documento</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Nombre completo candidato</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Prerregistro</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Fecha de prerregistro</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Fecha asistencia</h4>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {asistentes.length > 0 ? (
                    asistentes.map((row, index) => (
                      <TableRow
                        key={row.id || row.user_id || index}
                        sx={{
                          backgroundColor: index % 2 === 1 ? '#F4ECE1' : '#FFFFFF',
                          '&:hover': { backgroundColor: '#E2E8F0' },
                        }}
                      >
                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.tipo_documento === 'CC' ? 'C.C.' : row.tipo_documento}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.numero_documento}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.nombre_completo}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.realizo_prerregistro === 'SI' ? 'Sí' : 'No'}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {formatFecha(row.fecha_prerregistro)}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {formatFecha(row.fecha_asistencia)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{
                          py: 3,
                          fontFamily: 'Poppins',
                          fontSize: '14.5px',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                          }}
                        >
                          {loading
                            ? 'Cargando asistentes...'
                            : 'No se encontraron registros de asistencia para este evento.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </div>

        {/* PAGINACIÓN */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              shape="rounded"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .Mui-selected': {
                  backgroundColor: '#34495E !important',
                  color: '#FFF',
                },
              }}
            />
          </Box>
        )}

        {/* NOTIFICACIÓN TOAST (ESQUINA SUPERIOR DERECHA) */}
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={open}
          onClose={() => setOpen(false)}
        >
          <Alert
            onClose={() => setOpen(false)}
            variant="filled"
            severity={alert ? 'success' : 'error'}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
            {alertMsg}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default VerCandidatosAsistentesEventoPage;